<template>
  <el-dropdown trigger="click" @command="handleCommand">
    <el-button type="primary" class="dropdown-button" :loading="loading">
      {{ $t('Integrations_Button_Actions') }}
      <i class="dropdown-icon el-icon-arrow-down el-icon--right"></i>
    </el-button>

    <el-dropdown-menu>
      <el-dropdown-item
        v-for="(action, index) in integration.actions"
        v-if="
          !action.invisibleForRoutes ||
            !action.invisibleForRoutes.includes(currentRouteName)
        "
        :key="index"
        :icon="action.icon"
        :disabled="isDisabled(action)"
        :command="{
          type: action.action || 'action',
          index,
          eventName: action.eventName || 'integration-common'
        }"
      >
        {{ $t(action.label) }}
      </el-dropdown-item>

      <el-dropdown-item
        v-if="model.active && deactivateEndpoint"
        icon="el-icon-turn-off"
        :command="{ type: 'toggle' }"
      >
        {{ $t('Integrations_Button_Deactivate') }}
      </el-dropdown-item>

      <el-dropdown-item
        v-else-if="!model.active && activateEndpoint"
        icon="el-icon-open"
        :command="{ type: 'toggle' }"
      >
        {{ $t('Integrations_Button_Activate') }}
      </el-dropdown-item>

      <el-dropdown-item
        v-if="deleteEndpoint"
        icon="el-icon-delete"
        :command="{ type: 'delete' }"
      >
        {{ $t('Integrations_Button_Delete') }}
      </el-dropdown-item>
    </el-dropdown-menu>

    <div
      v-for="(action, index) in integration.actions"
      :key="index"
      :is="action.dialog"
      :model="model"
      :ref="`action_${index}_dialog`"
    ></div>
  </el-dropdown>
</template>

<script>
import RestApi from '@/core/services/RestApi'
import { projectEndpoint, projectRoute } from '@/core/helpers'
import { EventBus } from '@/core/EventBus'
import _ from 'lodash'

export default {
  name: 'IntegrationActionsDropdown',

  props: {
    integration: {
      type: Object,
      required: true
    },

    model: {
      type: Object,
      required: true
    }
  },

  data() {
    return {
      loading: false,
      checkDeletion: this.model.checkDeletion
    }
  },
  computed: {
    deleteEndpoint() {
      return projectEndpoint({
        method: 'delete',
        url: `integrations/${this.model.id}`,
        data: {
          checkDeletion: this.checkDeletion
        }
      })
    },

    updateEndpoint() {
      return projectEndpoint({
        method: 'patch',
        url: `integrations/${this.model.id}`
      })
    },

    deactivateEndpoint() {
      return projectEndpoint({
        method: 'post',
        url: `integrations/${this.model.id}/deactivate`
      })
    },

    activateEndpoint() {
      return projectEndpoint({
        method: 'post',
        url: `integrations/${this.model.id}/activate`
      })
    },

    deleteDialogMessage() {
      return this.integration.deletePrompt
        ? this.$t(this.integration.deletePrompt) +
            '<br>' +
            this.$t('Integrations_DialogDelete_Prompt')
        : this.$t('Integrations_DialogDelete_Prompt')
    },

    deactivateDialogMessage() {
      return this.integration.deactivatePrompt
        ? this.$t(this.integration.deactivatePrompt) +
            '<br>' +
            this.$t('Integrations_DialogDeactivate_Prompt')
        : this.$t('Integrations_DialogDeactivate_Prompt')
    },

    currentRouteName() {
      return this.$route.name
    }
  },

  methods: {
    handleCommand(command) {
      switch (command.type) {
        case 'delete':
          this.confirmDelete()
          break
        case 'toggle':
          this.confirmStatusChange()
          break
        case 'action':
          this.showActionDialog(command.index)
          break
        case 'event-bus-emit':
          EventBus.$emit(command.eventName, this.integration)
      }
    },

    confirmDelete() {
      this.$msgbox({
        title: this.$t('Integrations_DialogDelete_Title'),
        message: this.deleteDialogMessage,
        showCancelButton: true,
        confirmButtonText: this.$t('Integrations_Button_Confirm'),
        cancelButtonText: this.$t('Integrations_Button_Cancel'),
        dangerouslyUseHTMLString: true,
        center: true
      }).then(() => {
        this.loading = true
        RestApi(this.deleteEndpoint)
          .then(response => {
            this.loading = false
            if (response.data && response.data.status === 'error') {
              this.$msgbox({
                title: this.$t('Integrations_DialogDelete_Error_Title'),
                message: response.data.message,
                showCancelButton: true,
                confirmButtonText: this.$t('Integrations_Button_Confirm'),
                cancelButtonText: this.$t('Integrations_Button_Cancel'),
                center: true,
                dangerouslyUseHTMLString: true
              })
                .then(() => {
                  this.loading = true
                  this.checkDeletion = false
                  RestApi(this.deleteEndpoint).then(() => {
                    this.endDeletionProcess()
                  })
                })
                .catch(() => {
                  this.loading = false
                })
            } else {
              this.$store.dispatch('reloadProjectConfig')
              this.endDeletionProcess()
            }
          })
          .catch(error => {
            this.$message({
              message: error.message,
              type: 'error'
            })
            this.loading = false
          })
      })
    },

    endDeletionProcess() {
      this.$message({
        message: this.$t('Integrations_Message_DeleteSuccess'),
        type: 'success'
      })

      this.loading = false

      this.$router.push(
        projectRoute({
          name: 'IntegrationsList'
        })
      )
    },

    confirmStatusChange() {
      this.$msgbox({
        title: this.model.active
          ? this.$t('Integrations_DialogDeactivate_Title')
          : this.$t('Integrations_DialogActivate_Title'),
        message: this.model.active
          ? this.deactivateDialogMessage
          : this.$t('Integrations_DialogActivate_Prompt'),
        showCancelButton: true,
        confirmButtonText: this.$t('Integrations_Button_Confirm'),
        cancelButtonText: this.$t('Integrations_Button_Cancel'),
        dangerouslyUseHTMLString: true,
        center: true
      }).then(() => {
        this.loading = true
        let newStatus = !this.model.active
        RestApi({
          ...(this.model.active
            ? this.deactivateEndpoint
            : this.activateEndpoint)
        })
          .then(() => {
            this.$store.dispatch('reloadProjectConfig')
            this.model.active = newStatus
            this.$emit('update')
            EventBus.$emit('integration-status-changed', newStatus)
            this.$message({
              message: newStatus
                ? this.$t('Integrations_Message_ActivateSuccess')
                : this.$t('Integrations_Message_DeactivateSuccess'),
              type: 'success'
            })
            this.loading = false
          })
          .catch(error => {
            this.$message({
              message: error.message,
              type: 'error'
            })
            this.loading = false
          })
      })
    },

    showActionDialog(index) {
      this.$refs['action_' + index + '_dialog'][0].show()
    },

    isDisabled(action) {
      if (action.disabled_path) {
        return !_.get(this.model, action.disabled_path, true)
      }
    }
  }
}
</script>

<style scoped>
.dropdown-button {
  padding-left: 20px;
  padding-right: 10px;
}
.dropdown-icon {
  margin-left: 10px;
}
>>> .el-dropdown-menu__item i {
  margin-right: 15px;
}
</style>



// WEBPACK FOOTER //
// src/modules/integrations/components/IntegrationActionsDropdown.vue